import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { clearCategoryCaches } from "@/services/blog/cache/cacheStore";

interface CategoryInfo {
  id: number;
  name: string;
  count?: number;
}

interface CategoriesFilterProps {
  categories: CategoryInfo[];
  selectedCategoryId: number | null;
  onCategoryClick: (categoryId: number, categoryName: string) => void;
  onResetFilters: () => void;
}

export const CategoriesFilter = ({ 
  categories,
  selectedCategoryId,
  onCategoryClick,
  onResetFilters
}: CategoriesFilterProps) => {
  const navigate = useNavigate();
  
  if (categories.length === 0) {
    return null;
  }
  
  // Gestione diretta del click sulla categoria con navigazione
  const handleCategoryClick = (categoryId: number, categoryName: string) => {
    console.log(`[CategoriesFilter] Navigazione diretta alla categoria ${categoryId} (${categoryName})`);
    
    // Se è già selezionata, deseleziona (torna alla pagina blog senza parametri)
    if (selectedCategoryId === categoryId) {
      navigate('/blog');
      onResetFilters();
    } else {
      // Altrimenti naviga alla pagina con il parametro categoria
      navigate(`/blog?category=${categoryId}`);
      
      // Chiama anche il gestore originale per aggiornare lo stato locale
      onCategoryClick(categoryId, categoryName);
    }
  };
  
  // Gestione diretta del reset filtri con navigazione
  const handleResetFilters = () => {
    console.log('[CategoriesFilter] Reset filtri con navigazione diretta');
    
    // Clear category caches on filter reset
    clearCategoryCaches();
    
    // Naviga alla pagina blog senza parametri
    navigate('/blog');
    
    // Chiama anche il gestore originale
    onResetFilters();
  };
  
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map(category => {
        // Ensure ID is a number
        const categoryId = typeof category.id === 'string' ? parseInt(category.id, 10) : category.id;
        const isSelected = selectedCategoryId === categoryId;
        
        // Debug log for category selection state
        if (isSelected) {
          console.log(`[CategoriesFilter] Category ${categoryId} (${category.name}) is selected`);
        }
        
        return (
          <Badge 
            key={categoryId}
            variant={isSelected ? "default" : "outline"}
            className={`cursor-pointer hover:bg-muted transition-colors ${
              isSelected ? "bg-gold text-primary-foreground" : ""
            }`}
            onClick={() => handleCategoryClick(categoryId, category.name)}
          >
            {category.name}
            {category.count !== undefined && category.count > 0 && (
              <span className="ml-1 text-xs opacity-70">({category.count})</span>
            )}
          </Badge>
        );
      })}
      
      {(selectedCategoryId || categories.length > 0) && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleResetFilters}
          className="h-7 px-2"
        >
          Azzera filtri
        </Button>
      )}
    </div>
  );
};
